// ===========================================
// A/R AGING BY CATEGORY — where the receivable risk sits, by sales channel
// ===========================================
// The "Owed to you" card answers WHO owes; this compact table answers WHERE:
// open balances bucketed current / 1–30 / 31–60 / 61–90 / 90+ days past due,
// one row per sales category (the customer's crm.category, derived
// server-side). Everything is client-side math over the invoices already on
// the page. Amounts stay in their own currency — dollars and kroner never
// silently sum into one number.

import clsx from 'clsx';
import { formatByCurrency } from '../../lib/money';

const BUCKETS = ['current', 'b1_30', 'b31_60', 'b61_90', 'b90'] as const;
type Bucket = typeof BUCKETS[number];
const BUCKET_LABELS: Record<Bucket, string> = { current: 'Current', b1_30: '1–30', b31_60: '31–60', b61_90: '61–90', b90: '90+' };
// Mirrors the API's sales-category enum (same labels the Customers tab shows).
const CATEGORY_LABELS: Record<string, string> = {
  retail: 'Retail', ecommerce: 'E-commerce', food_service: 'Food service', distribution: 'Distribution',
  uncategorized: 'No category yet',
};
const CATEGORY_ORDER = ['retail', 'ecommerce', 'food_service', 'distribution', 'uncategorized'];

export default function ArAgingByCategory({ invoices, customers }: { invoices: any[]; customers: any[] }) {
  const catById = new Map<string, string>((customers || []).map((c: any) => [c.id, c.category || 'uncategorized']));
  const open = (invoices || []).filter((i: any) =>
    i.status !== 'paid' && i.status !== 'void' && ((i.totalCents || 0) - (i.amountPaidCents || 0)) > 0);
  if (open.length === 0) return null;

  type Row = Record<Bucket, Record<string, number>>;
  const mkRow = (): Row => ({ current: {}, b1_30: {}, b31_60: {}, b61_90: {}, b90: {} });
  const rows = new Map<string, Row>();
  const total = mkRow();
  const add = (m: Record<string, number>, cur: string, v: number) => { m[cur] = (m[cur] || 0) + v; };
  for (const i of open) {
    const owed = (i.totalCents || 0) - (i.amountPaidCents || 0);
    // Aged from the due date — same clock as the "Owed to you" card above
    // (issue date stands in when the invoice carries no terms).
    const days = Math.floor((Date.now() - new Date(i.dueAt || i.issuedAt).getTime()) / 86400000);
    const bucket: Bucket = days <= 0 ? 'current' : days <= 30 ? 'b1_30' : days <= 60 ? 'b31_60' : days <= 90 ? 'b61_90' : 'b90';
    const cat = catById.get(i.customerId) || 'uncategorized';
    const cur = String(i.currencyCode || 'USD').toUpperCase();
    const row = rows.get(cat) || mkRow();
    add(row[bucket], cur, owed);
    rows.set(cat, row);
    add(total[bucket], cur, owed);
  }
  const shown = CATEGORY_ORDER.filter((c) => rows.has(c));

  const cell = (m: Record<string, number>, b: Bucket) =>
    Object.keys(m).length === 0
      ? <span className="text-slate-300">—</span>
      : <span className={clsx('font-medium',
          b === 'b90' || b === 'b61_90' ? 'text-rose-600' : b === 'b31_60' ? 'text-amber-600' : 'text-slate-900')}>
          {formatByCurrency(m, { exact: true })}
        </span>;

  return (
    <div className="card p-0 overflow-x-auto">
      <div className="px-4 py-2.5 bg-slate-50">
        <p className="text-sm font-semibold text-slate-700">Aging by sales category</p>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase text-slate-400 border-b border-slate-100">
            <th className="px-4 py-2 font-medium text-left">Category</th>
            {BUCKETS.map((b) => <th key={b} className="px-3 py-2 font-medium text-right whitespace-nowrap">{BUCKET_LABELS[b]}</th>)}
          </tr>
        </thead>
        <tbody>
          {shown.map((cat) => {
            const r = rows.get(cat)!;
            return (
              <tr key={cat} className="border-b border-slate-50">
                <td className="px-4 py-2 text-slate-700 whitespace-nowrap">{CATEGORY_LABELS[cat] || cat}</td>
                {BUCKETS.map((b) => <td key={b} className="px-3 py-2 text-right whitespace-nowrap">{cell(r[b], b)}</td>)}
              </tr>
            );
          })}
          {shown.length > 1 && (
            <tr className="bg-slate-50/60">
              <td className="px-4 py-2 font-semibold text-slate-700 whitespace-nowrap">All categories</td>
              {BUCKETS.map((b) => <td key={b} className="px-3 py-2 text-right whitespace-nowrap">{cell(total[b], b)}</td>)}
            </tr>
          )}
        </tbody>
      </table>
      <p className="px-4 py-2 text-[11px] text-slate-400">
        Open invoices grouped by each customer's sales category, aged from the due date. "Current" isn't due yet; the other columns are days past due.
      </p>
    </div>
  );
}
